import type { ThemeTypings } from '@chakra-ui/react';
import { Flex, Grid, Text, chakra, useBreakpointValue, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { AddressParam } from 'types/api/addressParams';

import type { EntityProps } from 'ui/shared/entities/address/AddressEntity';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import AddressEntityWithTokenFilter from 'ui/shared/entities/address/AddressEntityWithTokenFilter';

import AddressFromToIcon from './AddressFromToIcon';
import { getTxCourseType } from './utils';

type Mode = 'compact' | 'long';

interface Props {
  from: AddressParam;
  to: AddressParam | null;
  current?: string;
  mode?: Mode | Partial<Record<ThemeTypings['breakpoints'], Mode>>;
  className?: string;
  isLoading?: boolean;
  tokenHash?: string;
  truncation?: EntityProps['truncation'];
  noIcon?: boolean;
  variant?: 'default' | 'explorer';
  fromLabel?: string;
  toLabel?: string;
}

const AddressFromTo = ({
  from,
  to,
  current,
  mode: modeProp,
  className,
  isLoading,
  tokenHash = '',
  variant = 'default',
  fromLabel = 'From',
  toLabel = 'To',
}: Props) => {
  const mode = useBreakpointValue(
    {
      base: (typeof modeProp === 'object' ? modeProp.base : modeProp),
      lg: (typeof modeProp === 'object' ? modeProp.lg : modeProp),
      xl: (typeof modeProp === 'object' ? modeProp.xl : modeProp),
    },
  ) ?? 'long';

  const fromToLabelColor = useColorModeValue('#616161', '#999999');
  const compactDefaultAddressSx = useColorModeValue(
    {
      a: {
        color: 'link',
        _hover: { color: 'link_hovered' },
      },
      button: {
        color: '#14DCAC',
      },
    },
    {
      a: {
        color: '#C2FFF0',
        _hover: { color: '#C2FFF0' },
      },
      button: {
        color: '#14DCAC',
      },
    },
  );
  const compactExplorerAddressSx = useColorModeValue(
    {
      a: {
        color: 'link',
        _hover: { color: 'link_hovered' },
        fontFamily: '\'Oxanium\', var(--chakra-fonts-body)',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '20px',
      },
      button: {
        color: '#14DCAC',
      },
    },
    {
      a: {
        color: '#FFFFFF',
        _hover: { color: '#FFFFFF' },
        fontFamily: '\'Oxanium\', var(--chakra-fonts-body)',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '20px',
      },
      button: {
        color: '#14DCAC',
      },
    },
  );

  const Entity = tokenHash ? AddressEntityWithTokenFilter : AddressEntity;

  if (mode === 'compact') {
    if (variant === 'explorer') {
      return (
        <Flex className={ className } flexDir="column" gap="4px">
          <Flex alignItems="center" gap="4px" minW={ 0 }>
            <Text color={ fromToLabelColor } fontSize="14px" lineHeight="20px" fontWeight={ 500 } fontFamily="'Space Grotesk', var(--chakra-fonts-body)">
              { fromLabel }
            </Text>
            <Entity
              address={ from }
              isLoading={ isLoading }
              noLink={ current === from.hash }
              noIcon
              tokenHash={ tokenHash }
              truncation="constant_tokens"
              sx={ compactExplorerAddressSx }
            />
          </Flex>
          { to && (
            <Flex alignItems="center" gap="4px" minW={ 0 }>
              <Text color={ fromToLabelColor } fontSize="14px" lineHeight="20px" fontWeight={ 500 } fontFamily="'Space Grotesk', var(--chakra-fonts-body)">
                { toLabel }
              </Text>
              <Entity
                address={ to }
                isLoading={ isLoading }
                noLink={ current === to.hash }
                noIcon
                tokenHash={ tokenHash }
                truncation="constant_tokens"
                sx={ compactExplorerAddressSx }
              />
            </Flex>
          ) }
        </Flex>
      );
    }

    return (
      <Flex className={ className } flexDir="column" gap="4px">
        <Flex alignItems="center" gap="4px">
          <Entity
            label={ fromLabel }
            address={ from }
            isLoading={ isLoading }
            noLink={ current === from.hash }
            noIcon
            tokenHash={ tokenHash }
            truncation="constant_tokens"
            sx={ compactDefaultAddressSx }
          />
        </Flex>
        { to && (
          <Flex alignItems="center" gap="4px">
            <Entity
              label={ toLabel }
              address={ to }
              isLoading={ isLoading }
              noLink={ current === to.hash }
              noIcon
              tokenHash={ tokenHash }
              truncation="constant_tokens"
              sx={ compactDefaultAddressSx }
            />
          </Flex>
        ) }
      </Flex>
    );
  }

  const isOutgoing = current === from.hash;
  const iconSize = 20;

  return (
    <Grid className={ className } alignItems="center" gridTemplateColumns={ `minmax(auto, min-content) ${ iconSize }px minmax(auto, min-content)` }>
      <Entity
        address={ from }
        isLoading={ isLoading }
        noLink={ isOutgoing }
        noCopy={ isOutgoing }
        // noIcon={ noIcon }
        noIcon
        tokenHash={ tokenHash }
        truncation="constant"
        mr={ isOutgoing ? 4 : 2 }
      />
      <AddressFromToIcon
        isLoading={ isLoading }
        type={ getTxCourseType(from.hash, to?.hash, current) }
      />
      { to && (
        <Entity
          address={ to }
          isLoading={ isLoading }
          noLink={ current === to.hash }
          noCopy={ current === to.hash }
          // noIcon={ noIcon }
          noIcon
          tokenHash={ tokenHash }
          truncation="constant"
          ml={ 3 }
        />
      ) }
    </Grid>
  );
};

export default chakra(AddressFromTo);
